import AddSvg from "@/src/assets/icons/icon-add-black.svg";
import ChevronLeftSvg from "@/src/assets/icons/icon-chevron-left-black.svg";
import { Button } from "@/src/components/atom/Button";
import Icon from "@/src/components/atom/Icon";
import Loader from "@/src/components/atom/Loader";
import Table from "@/src/components/atom/Table";
import Typo from "@/src/components/atom/Typo";
import AddressFormItem from "@/src/components/molecule/AddressFormItem";
import BottomFixedContainer from "@/src/components/molecule/BottomFixedContainer";
import ContactFormItem from "@/src/components/molecule/ContactFormItem";
import CountryWithTimezoneFormItem from "@/src/components/molecule/CountryWithTimezoneFormItem";
import FormItem, { FormItemProps } from "@/src/components/molecule/FormItem";
import FormInputPropsWithInputType from "@/src/components/molecule/FormItem/type";
import SectionCard from "@/src/components/molecule/SectionCard";
import ExporterMainLayout from "@/src/components/template/Layout/exporter/ExporterMainLayout";
import useAgGridHeaderRefresh from "@/src/hooks/useAgGridHeaderRefresh";
import useAlert from "@/src/hooks/useAlert";
import useContentLoading from "@/src/hooks/useContentLoading";
import EXPORTER_PRIVATE_PATH from "@/src/routes/exporter/path";
import { useGetSessionQuery } from "@/src/store/apis/auth";
import {
  useCreateFactoryEmployeesMutation,
  useEditFactoryEmployeesMutation,
  useEditFactoryMutation,
  useGetFactoryQuery,
  useLazyGetFactoryEmployeesQuery,
} from "@/src/store/apis/client/factory";
import { useGetCommonCodeViaCodeNameQuery } from "@/src/store/apis/common";
import { mediaQuery } from "@/src/styles/mediaQuery";
import typo from "@/src/styles/typography";
import { isUndefined } from "@/src/utils/is";
import { editAction } from "@/src/utils/row-data-util";
import { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { styled } from "styled-components";
import { editFactoryContactPersonListColumn } from "./columns/column";
import ContactPersonAddDialog, {
  TransformContactPersonAddModalFormType,
} from "./dialog/ContactPersonAddDialog";
import ContactPersonEditDialog, {
  ContactPersonEditFormType,
} from "./dialog/ContactPersonEditDialog";

type BasicInformationType = {
  countryCodeItemName: string;
  workplaceName: string;
  isActivated: string;
  apartment: string;
  city: string;
  state: string;
  postal: string;
  country: string;
  telPrefix: string;
  tel: string;
  faxPrefix: string;
  fax: string;
  businessNumber: string;
  businessRegistration: any;
  mainCategoryCodeItemNames: string[];
  timezoneCity: string;
  timezoneGmtOffset: number;
};

enum DialogState {
  NULL,
  ADD,
  EDIT,
}

const ExporterFactoryManagementEditPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const agGridRef = useRef<AgGridReact>(null);
  const { handleContentLoadingOff, handleContentLoadingOn } =
    useContentLoading();
  const pageParams = useParams<{ id: string }>();
  const editButtonRef = useRef<HTMLButtonElement | null>(null);
  const [dialogState, setDialogState] = useState<DialogState>(DialogState.NULL);
  const [targetEditId, setTargetEditId] = useState<null | number>(null);
  const [isReady, setIsReady] = useState<boolean>(false);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 10 });
  const [columnDefs] = useState<ColDef[]>(editFactoryContactPersonListColumn);
  const alert = useAlert();

  // API
  const { exporterUserType, userMainCategory } = useGetSessionQuery(undefined, {
    refetchOnMountOrArgChange: true,
    selectFromResult: ({ currentData, isError, isFetching }) => {
      const isUnstable = isError || isFetching || isUndefined(currentData);
      const isStable = !isUnstable;

      return {
        userMainCategory: isStable
          ? currentData.row.mainCategoryCodeItemNames
          : [],
        exporterUserType: currentData?.row.exporterUserType,
      };
    },
  });
  const factoryQuery = useGetFactoryQuery(
    {
      id: Number(pageParams.id),
    },
    {
      refetchOnMountOrArgChange: true,
      selectFromResult: ({ currentData, isFetching, isError }) => {
        const isUnstable = isFetching || isError || isUndefined(currentData);
        const isStable = !isUnstable;

        return {
          isFactoryInfoFetching: isFetching,
          mainCategoryCodeItemNames: isStable
            ? currentData.mainCategoryCodeItemNames
            : [],
          countryCodeItemName: isStable
            ? currentData.workplaceQueryResultDto.country.codeItemName
            : "",
          workplaceName: isStable ? currentData.workplaceName : "",
          isActivated: isStable ? currentData.isActivated : false,
          apartment: isStable
            ? currentData.workplaceQueryResultDto.workplaceStreetAddress
            : "",
          city: isStable
            ? currentData.workplaceQueryResultDto.workplaceLocality
            : "",
          state: isStable
            ? currentData.workplaceQueryResultDto.workplaceRegion
            : "",
          postal: isStable
            ? currentData.workplaceQueryResultDto.workplacePostalCode
            : "",
          country: isStable
            ? currentData.workplaceQueryResultDto.workplaceCountryName
            : "",
          telPrefix: isStable
            ? currentData.workplaceQueryResultDto.telPrefix
            : "",
          tel: isStable ? currentData.workplaceQueryResultDto.tel : "",
          faxPrefix: isStable
            ? currentData.workplaceQueryResultDto.faxPrefix
            : "",
          fax: isStable ? currentData.workplaceQueryResultDto.fax : "",
          businessNumber: isStable ? currentData.businessNumber : "",
          businessRegistration: isStable
            ? currentData.businessRegistrationSimpleMedia
            : undefined,
          timezoneCity: isStable ? currentData.city : "",
          timezoneGmtOffset: isStable ? currentData.gmtOffset : 0,
        };
      },
    }
  );
  const [createEmployee] = useCreateFactoryEmployeesMutation();
  const [editEmployee] = useEditFactoryEmployeesMutation();
  const [editFactory] = useEditFactoryMutation();
  const { countryList } = useGetCommonCodeViaCodeNameQuery(
    {
      codeName: "COUNTRY",
    },
    {
      refetchOnMountOrArgChange: true,
      selectFromResult: ({ isFetching, isError, currentData }) => {
        const isUnstable = isFetching || isError || isUndefined(currentData);
        const isStable = !isUnstable;

        return {
          isCountryFetching: isFetching,
          countryList: isStable ? currentData : [],
        };
      },
    }
  );
  const { isMainCategoryFetching, mainCategory } =
    useGetCommonCodeViaCodeNameQuery(
      {
        codeName: "MAIN_CATEGORY",
      },
      {
        refetchOnMountOrArgChange: true,
        selectFromResult: ({ currentData, isError, isFetching }) => {
          const isUnstable = isError || isFetching || currentData === undefined;
          const isStable = !isUnstable;

          return {
            isMainCategoryFetching: isFetching,
            mainCategory: isStable ? currentData : [],
          };
        },
      }
    );

  const {
    control,
    register,
    watch,
    setValue,
    setFocus,
    handleSubmit: handleBasicInfoSubmit,
    getValues,
  } = useForm<BasicInformationType>({
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: {
      mainCategoryCodeItemNames: factoryQuery.mainCategoryCodeItemNames,
      countryCodeItemName: factoryQuery.countryCodeItemName,
      workplaceName: factoryQuery.workplaceName,
      isActivated: factoryQuery.isActivated ? "Y" : "N",
      apartment: factoryQuery.apartment,
      city: factoryQuery.city,
      state: factoryQuery.state,
      postal: factoryQuery.postal,
      country: factoryQuery.country,
      telPrefix: factoryQuery.telPrefix,
      tel: factoryQuery.tel,
      faxPrefix: factoryQuery.faxPrefix,
      fax: factoryQuery.fax,
      businessNumber: factoryQuery.businessNumber,
      businessRegistration: factoryQuery.businessRegistration
        ? [factoryQuery.businessRegistration]
        : [],
      timezoneCity: factoryQuery.timezoneCity,
      timezoneGmtOffset: factoryQuery.timezoneGmtOffset,
    },
  });

  const mainCategoryOptionList = useMemo(
    () =>
      mainCategory.map((item) => {
        return {
          label: item.codeItemNameEn,
          value: item.codeItemName,
        };
      }),
    [mainCategory]
  );

  const mainCategoryStringList = useMemo(
    () => mainCategory.map((item) => item.codeItemName),
    [mainCategory]
  );

  const [
    getFactoryEmployees,
    { employees, isEmployeesFetching, employeesCount },
  ] = useLazyGetFactoryEmployeesQuery({
    selectFromResult: ({ isFetching, isError, currentData }) => {
      const isUnstable = isUndefined(currentData) || isFetching || isError;
      const isStable = !isUnstable;

      return {
        employees: isStable ? currentData.rows : [],
        isEmployeesFetching: isFetching,
        employeesCount: isStable ? currentData.count : 0,
      };
    },
  });

  useAgGridHeaderRefresh({
    gridRef: agGridRef.current,
    isReady,
    headerSet: [
      { columnKey: "name", langKey: "factoryManagement:common.name" },
      { columnKey: "email", langKey: "factoryManagement:common.email" },
      {
        columnKey: "personalContact",
        langKey: "factoryManagement:common.personalContact",
      },
      {
        columnKey: "officeContact",
        langKey: "factoryManagement:common.officeContact",
      },
      { columnKey: "isActivated", langKey: "factoryManagement:common.use" },
      { columnKey: "edit", langKey: "factoryManagement:common.edit" },
    ],
  });

  const basicInformationFormItemList: (Partial<
    FormItemProps<BasicInformationType, keyof BasicInformationType> &
      FormInputPropsWithInputType
  > & {
    customRender?: () => ReactNode;
  })[] = [
    {
      label: t("factoryManagement:common.factoryName"),
      type: "text",
      name: "workplaceName",
      control: control,
      rules: { required: true, maxLength: 50 },
      errorsMessage: {
        required: t("error:required"),
        maxLength: t("error:maxLength50"),
      },
      inputProps: {
        placeholder: t("factoryManagement:placeholder.factoryName"),
      },
    },
    {
      customRender: () => (
        <StyledCountryWithTimezoneFormItem
          label={t("signupCorp:content.countryAndCity")}
          register={register as any}
          setValue={setValue as any}
          isRequired={true}
          countryName="countryCodeItemName"
          countryValue={watch("countryCodeItemName") || undefined}
          cityName="timezoneCity"
          cityValue={watch("timezoneCity") || undefined}
          gmtOffsetName="timezoneGmtOffset"
          errorMessage={t("error:required")}
          onCountryChange={(value) => {
            const prefix = countryList.find(
              ({ codeItemName }) => codeItemName === value
            )?.value1;
            setValue("telPrefix", prefix || "");
            setValue("faxPrefix", prefix || "");
          }}
        />
      ),
    },
    {
      label: t("factoryManagement:common.categoryInCharge"),
      type: "multipleSelect",
      name: "mainCategoryCodeItemNames",
      rules: { required: true },
      errorsMessage: {
        required: t("error:required"),
      },
      control: control,
      options: mainCategoryOptionList,
      inputProps: {
        disableValues:
          // 일반매니저, 중간관리자가 아닌 경우 필터링 없이 그대로 유지
          exporterUserType === "MANAGER" ||
          exporterUserType === "MIDDLE_MANAGER"
            ? mainCategoryStringList.filter(
                (category) => !userMainCategory.includes(category)
              )
            : undefined,
        placeholder: t("factoryManagement:placeholder.categoryInCharge"),
        suffixIcon: isMainCategoryFetching ? <Loader /> : undefined,
        disabled: isMainCategoryFetching,
        onRemoveItem: (item) => {
          setValue(
            "mainCategoryCodeItemNames",
            getValues("mainCategoryCodeItemNames").filter(
              (mainCategoryItem) => mainCategoryItem !== item
            )
          );
        },
      },
    },
    {
      label: t("factoryManagement:common.businessNumber"),
      type: "text",
      name: "businessNumber",
      control: control,
      inputProps: {
        placeholder: t("factoryManagement:placeholder.businessNumber"),
      },
    },
    {
      label: t("factoryManagement:common.businessRegistration"),
      type: "file",
      name: "businessRegistration",
      control: control,
      inputProps: {
        defaultFileList: watch("businessRegistration").length
          ? [
              new File(
                [""],
                watch("businessRegistration")?.[0]?.originalFileName ?? ""
              ),
            ]
          : undefined,
        onRemove: () => setValue("businessRegistration", []),
      },
    },
    {
      label: t("factoryManagement:common.use"),
      type: "radioGroup",
      name: "isActivated",
      control: control,
      rules: { required: true },
      options: [
        { label: "Y", value: "Y" },
        { label: "N", value: "N" },
      ],
    },
  ];

  const handleCreateEmployee = async (
    values: TransformContactPersonAddModalFormType
  ) => {
    try {
      await createEmployee({
        id: Number(pageParams.id) ?? "",
        isActivated: values.isActivated,
        name: values.name,
        email: values.email,
        personalContactPrefix: values.personalContactPrefix,
        personalContact: values.personalContact,
        officeContactPrefix: values.officeContactPrefix
          ? values.officeContactPrefix
          : undefined,
        officeContact: values.officeContact ? values.officeContact : undefined,
      }).unwrap();
      setDialogState(DialogState.NULL);
    } catch (e: any) {
      const message = Array.isArray(e.data.message)
        ? e.data.message[0]
        : e.data.message;
      alert.showAlert({ type: "error", message });
    }
  };

  const handleEditContactPerson = async (values: ContactPersonEditFormType) => {
    try {
      await editEmployee({
        id: Number(targetEditId) ?? "",
        name: values.name ?? "",
        email: values.email ?? "",
        isActivated: values.isActivated,
        personalContact: values.personalContact,
        personalContactPrefix: values.personalContactPrefix,
        officeContact: values.officeContact ? values.officeContact : null,
        officeContactPrefix: values.officeContactPrefix
          ? values.officeContactPrefix
          : null,
      }).unwrap();
      setDialogState(DialogState.NULL);
    } catch (e: any) {
      const message = Array.isArray(e.data.message)
        ? e.data.message[0]
        : e.data.message;
      alert.showAlert({ type: "error", message });
    }
  };

  const handleEditFactory = async () => {
    try {
      const {
        countryCodeItemName,
        workplaceName,
        isActivated,
        apartment,
        city,
        state,
        postal,
        country,
        telPrefix,
        tel,
        faxPrefix,
        fax,
        businessNumber,
        businessRegistration,
        mainCategoryCodeItemNames,
        timezoneCity,
        timezoneGmtOffset,
      } = getValues();

      const validBodyData = Object.entries({
        telPrefix: telPrefix || undefined,
        faxPrefix: faxPrefix || undefined,
        tel: tel || undefined,
        fax: fax || undefined,
      })
        .filter(([_key, value]) => value !== undefined) // undefined인 값 필터링
        .reduce<any>((acc, [key, value]) => {
          acc[key] = value; // 유효한 값만 추가
          return acc;
        }, {});

      await editFactory({
        id: Number(pageParams.id) ?? "",
        workplaceName,
        mainCategoryCodeItemNames,
        countryCodeItemName:
          countryList.find(
            ({ codeItemNameKo, codeItemName }) =>
              countryCodeItemName === codeItemNameKo ||
              countryCodeItemName === codeItemName
          )?.codeItemName ?? "",
        workplacePostalCode: postal || null,
        workplaceCountryName: country,
        workplaceRegion: state || null,
        workplaceLocality: city || null,
        workplaceStreetAddress: apartment || null,
        isActivated: isActivated === "Y",
        businessNumber: businessNumber || null,
        businessRegistrationMediaId: businessRegistration[0]?.id ?? null,
        city: timezoneCity,
        gmtOffset: timezoneGmtOffset,
        ...validBodyData,
      }).unwrap();

      navigate(
        `${EXPORTER_PRIVATE_PATH.FACTORY_MANAGEMENT_DETAIL}/${pageParams.id}`
      );
    } catch (e: any) {
      const message = Array.isArray(e.data.message)
        ? e.data.message[0]
        : e.data.message;
      alert.showAlert({ type: "error", message });
    }
  };

  const handleDetailClick = (data: any) => {
    setTargetEditId(data.id);
    setDialogState(DialogState.EDIT);
  };

  const fetchFactoryEmployees = async ({
    page,
    pageSize,
  }: {
    page?: number;
    pageSize?: number;
  }) => {
    try {
      await getFactoryEmployees({
        id: Number(pageParams.id),
        page,
        pageSize,
      }).unwrap();
    } catch (e: any) {
      const message = Array.isArray(e.data.message)
        ? e.data.message[0]
        : e.data.message;
      alert.showAlert({ type: "error", message });
    }
  };

  useEffect(() => {
    fetchFactoryEmployees({
      page: pagination.page,
      pageSize: pagination.pageSize,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setFocus("workplaceName");
  }, [setFocus]);

  useEffect(() => {
    if (factoryQuery.isFactoryInfoFetching) {
      handleContentLoadingOn();
    } else {
      handleContentLoadingOff();
    }
  }, [
    factoryQuery.isFactoryInfoFetching,
    handleContentLoadingOff,
    handleContentLoadingOn,
  ]);

  useEffect(() => {
    if (agGridRef.current && isEmployeesFetching) {
      agGridRef.current.api.showLoadingOverlay();
    }
  }, [isEmployeesFetching]);

  const renderDialog = () => {
    const defaultCountry = countryList.find(
      ({ codeItemName }) => getValues("countryCodeItemName") === codeItemName
    );

    if (dialogState === DialogState.NULL) return null;

    if (dialogState === DialogState.ADD) {
      return (
        <ContactPersonAddDialog
          defaultCountryCode={defaultCountry?.value1}
          onSave={handleCreateEmployee}
          onClose={() => setDialogState(DialogState.NULL)}
        />
      );
    }

    if (dialogState === DialogState.EDIT && targetEditId) {
      const employee = employees.find(({ id }) => id === targetEditId);

      if (!employee) return null;

      return (
        <ContactPersonEditDialog
          onSave={handleEditContactPerson}
          onClose={() => setDialogState(DialogState.NULL)}
          defaultContactPersonInfo={{
            name: employee?.name || "",
            email: employee?.email || "",
            personalContactPrefix: employee.personalContactPrefix,
            personalContact: employee.personalContact,
            isActivated: employee.isActivated ? "Y" : "N",
            officeContactPrefix:
              employee.officeContactPrefix || defaultCountry?.value1,
            officeContact: employee.officeContact,
          }}
        />
      );
    }
  };

  return (
    <ExporterMainLayout
      breadcrumb={[
        t("sideNav:factory"),
        t("sideNav:factoryManagementEdit"),
      ]}
      pageTitle={t("sideNav:factoryManagementEdit")}
    >
      <SectionContainer>
        <SectionCard cardTitle={t("factoryManagement:common.basicInformation")}>
          <StyledForm onSubmit={handleBasicInfoSubmit(handleEditFactory)}>
            <HalfDiv>
              {basicInformationFormItemList.map(
                ({
                  label,
                  type,
                  name,
                  control,
                  options,
                  rules,
                  inputProps,
                  disabled,
                  errorsMessage,
                  customRender,
                }) => {
                  if (customRender) {
                    return customRender();
                  }

                  return (
                    <StyledFormItem
                      label={label}
                      type={type as any}
                      name={name as keyof BasicInformationType}
                      control={control as any}
                      options={options ?? []}
                      inputProps={inputProps as any}
                      rules={rules}
                      disabled={disabled}
                      errorsMessage={errorsMessage}
                    />
                  );
                }
              )}
            </HalfDiv>
            <HalfDiv>
              <StyledAddressFormItem
                label={t("signupCorp:content.address")}
                register={register as any}
                isRequired
                setValue={setValue as any}
                apartmentValue={watch("apartment") || ""}
                cityValue={watch("city") || ""}
                stateValue={watch("state") || ""}
                postalValue={watch("postal") || ""}
                countryValue={watch("country") || ""}
                englishOnly={true}
              />
              <StyledContactFormItem
                label={t("signupCorp:content.contact")}
                prefixName="telPrefix"
                register={register as any}
                setValue={setValue as any}
                restContactName="tel"
                prefixValue={watch("telPrefix") || undefined}
                restContactValue={watch("tel") || ""}
                isRequired={false}
              />
              <StyledContactFormItem
                label={t("factoryManagement:common.fax")}
                prefixName="faxPrefix"
                register={register as any}
                setValue={setValue as any}
                restContactName="fax"
                prefixValue={watch("faxPrefix") || undefined}
                restContactValue={watch("fax") || ""}
                isRequired={false}
              />
            </HalfDiv>
            <Hidden ref={editButtonRef} />
          </StyledForm>
        </SectionCard>
        <SectionCard
          cardTitle={t("factoryManagement:common.contactPersonList")}
          rightAccessory={
            <StyledButton
              buttonGrade="tertiary"
              buttonSize={32}
              buttonColor="black"
              onClick={() => setDialogState(DialogState.ADD)}
            >
              <Icon iconSrc={AddSvg} iconSize={16} />
              <Typo typoType="btn3m" color="gray2">
                {t("factoryManagement:factoryListAddPage.addContactPerson")}
              </Typo>
            </StyledButton>
          }
        >
          <Table
            ref={agGridRef}
            rowData={isEmployeesFetching ? undefined : employees}
            columnDefs={columnDefs}
            components={{
              ...editAction({ onClick: handleDetailClick }),
            }}
            isPaginationDataMaping
            totalPage={employeesCount}
            page={pagination.page}
            pageSize={pagination.pageSize}
            onGridReady={() => setIsReady(true)}
            handlePaginationClick={(page, pageSize) => {
              fetchFactoryEmployees({ page, pageSize });
              setPagination({ page, pageSize });
            }}
          />
        </SectionCard>

        <BottomFixedContainer>
          <FooterButtonContainer>
            <FooterButton
              buttonColor="black"
              buttonGrade="tertiary"
              onClick={() => navigate(-1)}
              style={{ display: "flex", alignItems: "center", gap: "4px" }}
            >
              <Icon iconSrc={ChevronLeftSvg} iconSize={16} />
              {t("factoryManagement:common.backToPrevious")}
            </FooterButton>
            <FooterButton onClick={() => editButtonRef.current?.click()}>
              {t("factoryManagement:common.save")}
            </FooterButton>
          </FooterButtonContainer>
        </BottomFixedContainer>
      </SectionContainer>
      {renderDialog()}
    </ExporterMainLayout>
  );
};

export default ExporterFactoryManagementEditPage;

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 72px;
`;

const StyledForm = styled.form`
  display: flex;
  gap: 24px;
`;

const HalfDiv = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const StyledFormItem = styled(FormItem)`
  ${mediaQuery.FORM_INPUT_TO_FULL_INPUT} {
    flex-direction: column;
    gap: 8px;
  }
`;

const StyledCountryWithTimezoneFormItem = styled(CountryWithTimezoneFormItem)`
  ${mediaQuery.FORM_INPUT_TO_FULL_INPUT} {
    flex-direction: column;
    gap: 8px;
  }
`;

const StyledAddressFormItem = styled(AddressFormItem)`
  ${mediaQuery.FORM_INPUT_TO_FULL_INPUT} {
    flex-direction: column;
    gap: 8px;
    > div {
      width: 100% !important;
    }
  }
`;
const StyledContactFormItem = styled(ContactFormItem)`
  ${mediaQuery.FORM_INPUT_TO_FULL_INPUT} {
    flex-direction: column;
    gap: 8px;
  }
`;
const StyledButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Hidden = styled.button`
  position: absolute;
  width: 0;
  height: 0;
  padding: 0;
  overflow: hidden;
  border: 0;
`;

const FooterButton = styled(Button)`
  min-width: 158px;
  text-align: center;
  ${typo.btn3m}
`;

const FooterButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
